import { createFileRoute } from "@tanstack/react-router";
import { MapPin, Phone, Mail, Clock, FileText, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { QuoteForm } from "@/components/QuoteForm";
import { Reveal } from "@/components/Reveal";
import { RelatedLinks } from "@/components/RelatedLinks";
import { IMAGES } from "@/lib/images";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import mapImg from "@/assets/bbn/bbn-nordic-karta-staffanstorp.jpg.asset.json";

const KONTAKT_FAQS: FaqItem[] = [
  {
    q: "Hur snabbt återkopplar ni?",
    a: "Vi återkopplar samma dag vardagar 08:00–15:30 om möjligt. Skickar du in ett meddelande utanför öppettider hör vi av oss första möjliga vardag.",
  },
  {
    q: "Vilka ärenden kan jag höra av mig om?",
    a: "Du kan fråga oss om fönsterfilm, solfilm, säkerhetsfilm och dekorfilm. Vi hjälper dig med offert, platsbesök, tekniska frågor och provbitar. Är du arkitekt, projektledare eller fastighetsägare får du också gärna höra av dig.",
  },
  {
    q: "Kommer ni ut på platsbesök?",
    a: "Ja, vi kommer ut för kostnadsfria platsbesök och rådgivning i Skåne, Göteborg och Stockholm. Vid längre resor kan reseersättning tillkomma. I så fall kommer vi överens om det i förväg.",
  },
  {
    q: "Kan jag komma förbi kontoret?",
    a: "Ja, du är välkommen till Satellitvägen 16 i Staffanstorp under öppettider (mån–fre 08:00–15:30). Vi rekommenderar att du bokar tid i förväg så vi kan avsätta rätt person för ditt ärende.",
  },
  {
    q: "Hur snabbt kan installationen ske?",
    a: "För standardprojekt är leveranstiden vanligtvis 2–4 veckor efter godkänd offert och uppmätning. Är det akut kan vi ofta hjälpa till tidigare. Ring oss på 0771 502 502.",
  },
];

export const Route = createFileRoute("/kontakt")({
  head: () => ({
    meta: [
      { title: "Kontakta BBN Nordic — fönsterfilm i Skåne | BBN Nordic" },
      { name: "description", content: "Ring 0771 502 502 eller 0737 49 09 02 eller mejla info@bbnnordic.se. Besöksadress: Satellitvägen 16, Staffanstorp." },
      { property: "og:title", content: "Kontakta BBN Nordic" },
      { property: "og:description", content: "Ring, mejla eller besök oss i Staffanstorp." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://bbnnordic.se/kontakt" },
      { property: "og:image", content: IMAGES.installer },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/kontakt" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Kontakta BBN Nordic",
          url: "https://bbnnordic.se/kontakt",
          mainEntity: {
            "@type": "LocalBusiness",
            "@id": "https://bbnnordic.se/#business",
            name: "BBN Nordic AB",
            telephone: ["+46771502502", "+46737490902"],
            email: "info@bbnnordic.se",
            url: "https://bbnnordic.se/",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Satellitvägen 16",
              postalCode: "245 34",
              addressLocality: "Staffanstorp",
              addressRegion: "Skåne",
              addressCountry: "SE",
            },
            openingHours: "Mo-Fr 08:00-15:30",
            areaServed: [
              { "@type": "AdministrativeArea", name: "Skåne" },
              { "@type": "City", name: "Göteborg" },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "Kontakt", item: "https://bbnnordic.se/kontakt" },
          ],

        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(KONTAKT_FAQS)) },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const items = [
    { icon: MapPin, label: "Adress", value: "Satellitvägen 16, 245 34 Staffanstorp" },
    { icon: Phone, label: "Telefon (växel)", value: "0771 502 502", href: "tel:+46771502502" },
    { icon: Phone, label: "Telefon (direkt)", value: "0737 49 09 02", href: "tel:+46737490902" },
    { icon: Mail, label: "E-post", value: "info@bbnnordic.se", href: "mailto:info@bbnnordic.se" },
    { icon: Clock, label: "Öppettider", value: "Mån–Fre 08:00–15:30" },
  ];

  return (
    <>
      <PageHero
        eyebrow="KONTAKT"
        title="Kontakta oss"
        subtitle="Ring, mejla eller fyll i kontaktformuläret — vi återkopplar så snart vi kan, vardagar 08:00–15:30."
        image={IMAGES.installer}
        breadcrumbs={[{ label: "Kontakt" }]}
      />

      {/* Quick-contact action row — primary path */}
      <section aria-label="Snabb kontakt" className="bg-bbn-tint border-b border-border">
        <div className="mx-auto max-w-7xl px-5 md:px-10 py-8 md:py-10">
          <div className="grid gap-3 md:gap-4 md:grid-cols-3">
            <a
              href="tel:+46771502502"
              className="group flex items-center gap-4 rounded-xl bg-bbn-accent text-white px-5 py-5 md:py-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/15">
                <Phone size={22} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-white/75">Ring direkt</span>
                <span className="block text-lg md:text-xl font-bold">0771 502 502</span>
              </span>
              <ArrowRight size={18} className="opacity-60 group-hover:translate-x-1 transition-transform" />
            </a>

            <a
              href="#kontaktformular"
              className="group flex items-center gap-4 rounded-xl bg-card border border-border px-5 py-5 md:py-6 hover:border-bbn-accent/40 hover:shadow-md transition-all"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-bbn-accent/10 text-bbn-accent">
                <FileText size={22} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Kontaktformulär</span>
                <span className="block text-lg md:text-xl font-bold text-foreground">Skicka meddelande</span>
              </span>
              <ArrowRight size={18} className="opacity-40 group-hover:translate-x-1 transition-transform text-muted-foreground" />
            </a>

            <a
              href="mailto:info@bbnnordic.se"
              className="group flex items-center gap-4 rounded-xl bg-card border border-border px-5 py-5 md:py-6 hover:border-bbn-accent/40 hover:shadow-md transition-all"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-bbn-accent/10 text-bbn-accent">
                <Mail size={22} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-muted-foreground">E-post</span>
                <span className="block text-base md:text-lg font-semibold text-foreground truncate">info@bbnnordic.se</span>
              </span>
              <ArrowRight size={18} className="opacity-40 group-hover:translate-x-1 transition-transform text-muted-foreground" />
            </a>
          </div>

          <p className="mt-4 text-sm text-muted-foreground flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-2"><Phone size={14} className="text-bbn-accent" /> Direkt: <a href="tel:+46737490902" className="hover:text-bbn-accent">0737 49 09 02</a></span>
            <span className="inline-flex items-center gap-2"><Clock size={14} className="text-bbn-accent" /> Mån–Fre 08:00–15:30</span>
            <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-bbn-accent" /> Satellitvägen 16, Staffanstorp</span>
          </p>
        </div>
      </section>



      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10 grid md:grid-cols-2 gap-12 md:gap-16">
          <Reveal>
            <div>
              <h2 className="text-2xl md:text-3xl mb-8">Kontaktuppgifter</h2>
              <ul className="space-y-6">
                {items.map((it) => (
                  <li key={it.label} className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-gold/15 text-gold">
                      <it.icon size={18} />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">{it.label}</div>
                      {it.href ? (
                        <a href={it.href} className="mt-1 block text-base md:text-lg hover:text-gold transition-colors">{it.value}</a>
                      ) : (
                        <div className="mt-1 text-base md:text-lg">{it.value}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div id="kontaktformular" className="scroll-mt-24">
              <h2 className="text-2xl md:text-3xl mb-8">Skicka meddelande</h2>
              <QuoteForm showCompanyAndSubject />
            </div>
          </Reveal>
        </div>
      </section>

      <section aria-label="Karta över Staffanstorp" className="pb-20 md:pb-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <a
            href="https://www.google.com/maps/place/BBN+Nordic+AB/@55.646479,13.2265395,17z"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Öppna BBN Nordic på Google Maps — Satellitvägen 16, Staffanstorp"
            className="group relative block rounded overflow-hidden border border-border aspect-[16/9] md:aspect-[21/9] focus:outline-none focus-visible:ring-2 focus-visible:ring-bbn-accent"
          >
            <img
              src={mapImg.url}
              alt="Karta som visar BBN Nordic AB på Satellitvägen 16 i Staffanstorp"
              loading="lazy"
              width={1110}
              height={900}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
              <div className="flex items-center justify-between gap-4 text-cream">
                <div className="min-w-0">
                  <div className="font-semibold truncate">BBN Nordic AB</div>
                  <div className="text-sm opacity-90 truncate">Satellitvägen 16, 245 34 Staffanstorp</div>
                </div>
                <span className="inline-flex items-center gap-2 rounded bg-bbn-accent text-navy px-3 py-1.5 text-sm font-semibold whitespace-nowrap">
                  Öppna i Maps →
                </span>
              </div>
            </div>
          </a>
        </div>
      </section>

      <FaqSection heading="Vanliga frågor om att kontakta oss." items={KONTAKT_FAQS} />



      <RelatedLinks
        items={[
          { to: "/offert", label: "Begär offert", desc: "Beskriv vad du vill ha pris på." },
          { to: "/radgivning", label: "Rådgivning & service", desc: "Läs vad som ingår i ett platsbesök." },
          { to: "/om-oss", label: "Om oss", desc: "Lär känna BBN Nordic." },
        ]}
      />
    </>
  );
}
